import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ApiFailure } from "../api/client";
import { useSession } from "../store/session";

// Pantalla de acceso.
//
// Es la única vista que ve alguien que todavía no es nadie para el sistema, y
// la única que se sirve a Internet sin sesión. De ahí las dos decisiones que
// la separan del resto del portal:
//
//   - **No usa los componentes de `ui.tsx`.** Esos están pensados para las
//     pantallas de trabajo, con fondo claro y densidad de tabla. Aquí no hay
//     datos que leer: hay dos campos y una marca.
//   - **Todo es autocontenido.** La CSP no permite tipografías ni imágenes
//     externas, así que el fondo son degradados y la animación es CSS. Cero
//     peticiones de más en la pantalla que más gente va a abrir.
//
// El segundo factor se pide en un segundo paso, cuando el backend contesta
// `MFA_REQUIRED`, y no con un campo siempre visible: el portal no puede saber
// quién tiene MFA antes de validar la contraseña, y preguntarlo de entrada
// sería enseñar un campo inútil a casi todo el mundo.
//
// El indicador de estado **no es decoración**: consulta `/health` de verdad.
// Sin él, una API caída se manifiesta como un login que «no acepta la
// contraseña», que es el peor diagnóstico posible.

type EstadoAPI = "consultando" | "en-linea" | "sin-conexion";

export function Login() {
  const { login, error } = useSession();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [codigo, setCodigo] = useState("");
  const [pideCodigo, setPideCodigo] = useState(false);
  // Aviso propio para el segundo factor: «falta el código» y «ese código no
  // sirve» son cosas distintas, y el store solo tiene un mensaje por código de
  // error. Tiene prioridad sobre `error` para no pintar dos recuadros.
  const [avisoMFA, setAvisoMFA] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [estado, setEstado] = useState<EstadoAPI>("consultando");

  // Otro correo es potencialmente otra persona, y puede no tener MFA: dejar el
  // campo del código puesto sería pedirle algo que no tiene.
  function cambiarCorreo(valor: string) {
    setEmail(valor);
    setPideCodigo(false);
    setCodigo("");
    setAvisoMFA(null);
  }

  useEffect(() => {
    let vivo = true;
    // `/health` es liveness y no toca dependencias: es seguro consultarlo sin
    // sesión y no dice nada del interior (CLAUDE.md §9).
    //
    // No basta con mirar el código: hay que comprobar que contestó **la API**.
    // Un 200 puede venir del propio servidor de estáticos devolviendo el index
    // cuando la ruta no está proxyeada, y entonces el indicador diría «en
    // línea» con el backend caído. Un indicador que miente es peor que no
    // tenerlo.
    fetch("/health", { method: "GET", headers: { Accept: "application/json" } })
      .then(async (r) => {
        if (!r.ok) return false;
        const cuerpo: unknown = await r.json();
        return (
          typeof cuerpo === "object" &&
          cuerpo !== null &&
          (cuerpo as { data?: { status?: string } }).data?.status === "ok"
        );
      })
      .then((ok) => vivo && setEstado(ok ? "en-linea" : "sin-conexion"))
      .catch(() => vivo && setEstado("sin-conexion"));
    return () => {
      vivo = false;
    };
  }, []);

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setAvisoMFA(null);
    try {
      await login(email, password, codigo);
      navigate("/", { replace: true });
    } catch (err) {
      // El mensaje ya quedó en el store, salvo el del segundo factor: ese se
      // redacta aquí porque depende de si ya se había escrito un código.
      if (err instanceof ApiFailure && err.error.code === "MFA_REQUIRED") {
        const habiaCodigo = codigo.trim() !== "";
        setAvisoMFA(
          habiaCodigo
            ? "Ese código no es válido o ya caducó. Espera al siguiente y vuelve a intentarlo."
            : "Esta cuenta tiene segundo factor. Escribe el código para entrar.",
        );
        setPideCodigo(true);
        // Un TOTP caducado no se reintenta: se teclea el siguiente. Y un
        // código de recuperación gastado ya no sirve nunca.
        setCodigo("");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#05070f] px-4 py-10">
      <Fondo />

      <div className="relative w-full max-w-[25rem]">
        <div className="borde-vivo rounded-2xl">
          <div className="rounded-2xl bg-white/[0.03] p-7 shadow-[0_0_60px_-15px_rgba(56,189,248,0.3)] backdrop-blur-xl sm:p-8">
            <Marca />
            <Onda activa={submitting} />

            <form onSubmit={onSubmit} className="mt-7 space-y-5">
              <Campo
                etiqueta="Correo"
                tipo="email"
                autoComplete="username"
                valor={email}
                onChange={cambiarCorreo}
                placeholder="tu@empresa.com"
              />
              <Campo
                etiqueta="Contraseña"
                tipo="password"
                autoComplete="current-password"
                valor={password}
                onChange={setPassword}
                placeholder="••••••••••••"
              />

              {pideCodigo && (
                <Campo
                  etiqueta="Código de verificación"
                  // `text` y no `number`: un código de recuperación lleva
                  // letras, y `number` además se come los ceros a la
                  // izquierda, que en un TOTP son significativos.
                  tipo="text"
                  autoComplete="one-time-code"
                  valor={codigo}
                  onChange={setCodigo}
                  placeholder="123456"
                  autoFocus
                  ayuda="Los seis dígitos de tu app de autenticación, o uno de tus códigos de recuperación."
                />
              )}

              {(avisoMFA ?? error) && (
                <p
                  role="alert"
                  className={
                    avisoMFA
                      ? // Ámbar y no rojo: pedir el código es un paso del
                        // acceso, no un fallo de quien escribe.
                        "rounded-lg border border-amber-400/30 bg-amber-400/10 px-3 py-2 text-sm text-amber-100"
                      : "rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-200"
                  }
                >
                  {avisoMFA ?? error}
                </p>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="group relative w-full overflow-hidden rounded-lg bg-gradient-to-r from-sky-500 to-indigo-500 px-4 py-2.5 text-sm font-semibold text-white shadow-[0_0_25px_-5px_rgba(56,189,248,0.6)] transition hover:shadow-[0_0_35px_-3px_rgba(56,189,248,0.85)] focus:outline-none focus:ring-2 focus:ring-sky-400 focus:ring-offset-2 focus:ring-offset-[#05070f] disabled:cursor-not-allowed disabled:opacity-60 disabled:shadow-none"
              >
                {submitting ? "Entrando…" : "Entrar"}
              </button>
            </form>

            <Estado estado={estado} />
          </div>
        </div>

        <p className="mt-6 text-center text-xs text-slate-600">
          Plataforma de voz programable · OMITECH
          {" · "}
          <a
            href="/docs"
            className="text-slate-500 underline decoration-slate-700 transition hover:text-sky-300"
          >
            Documentación
          </a>
        </p>
      </div>
    </div>
  );
}

/** Fondo: manchas de color, rejilla y viñeta. Puramente decorativo. */
function Fondo() {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0">
      {/* Las manchas entran a la pantalla en lugar de quedarse en las
          esquinas: ahí la viñeta se las comía y el fondo quedaba plano. */}
      <div className="aurora -left-10 top-[-6rem] h-[34rem] w-[34rem] bg-sky-500/50" />
      <div
        className="aurora -bottom-24 right-[-4rem] h-[36rem] w-[36rem] bg-indigo-600/50"
        style={{ animationDelay: "-11s" }}
      />
      <div
        className="aurora left-[38%] top-[8%] h-[22rem] w-[22rem] bg-fuchsia-500/30"
        style={{ animationDelay: "-5s" }}
      />
      <div className="rejilla absolute inset-0" />
      {/* Oscurece los bordes para que el formulario quede como lo más claro de
          la pantalla y el ojo vaya ahí sin pensarlo. Arranca tarde y cierra
          suave, o apaga las manchas en vez de enmarcarlas. */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_70%_60%_at_50%_45%,transparent_0%,rgba(5,7,15,0.55)_60%,#05070f_100%)]" />
    </div>
  );
}

function Marca() {
  return (
    <div>
      <div className="flex items-baseline gap-2.5">
        <span className="bg-gradient-to-br from-white to-sky-200 bg-clip-text text-2xl font-semibold tracking-tight text-transparent">
          SeekopIA
        </span>
        <span className="font-mono text-[0.7rem] uppercase tracking-[0.2em] text-sky-400/70">
          Voice API
        </span>
      </div>
      <p className="mt-1.5 text-sm text-slate-400">Entra para operar tus llamadas.</p>
    </div>
  );
}

/**
 * Onda: doce barras con alturas y retrasos propios.
 *
 * Es el motivo de la marca y no un adorno cualquiera: esto es una plataforma
 * de voz, y lo que se ve es una señal de audio. Mientras se envía el
 * formulario late más rápido, que es la única señal de progreso que tiene esta
 * pantalla.
 */
function Onda({ activa }: { activa: boolean }) {
  const alturas = [0.35, 0.6, 1, 0.75, 0.45, 0.9, 0.55, 1, 0.7, 0.4, 0.85, 0.5];

  return (
    <div aria-hidden className="mt-5 flex h-7 items-center gap-[3px]">
      {alturas.map((alto, i) => (
        <span
          key={i}
          className="barra-onda w-[3px] rounded-full bg-gradient-to-t from-sky-500/40 to-sky-300"
          style={{
            height: `${alto * 100}%`,
            animationDelay: `${i * 0.09}s`,
            animationDuration: activa ? "0.6s" : "1.3s",
          }}
        />
      ))}
    </div>
  );
}

function Campo({
  etiqueta,
  tipo,
  autoComplete,
  valor,
  onChange,
  placeholder,
  autoFocus,
  ayuda,
}: {
  etiqueta: string;
  tipo: "email" | "password" | "text";
  autoComplete: string;
  valor: string;
  onChange: (v: string) => void;
  placeholder: string;
  autoFocus?: boolean;
  ayuda?: string;
}) {
  return (
    <label className="block">
      <span className="text-xs font-medium uppercase tracking-wider text-slate-400">
        {etiqueta}
      </span>
      <input
        type={tipo}
        required
        // eslint-disable-next-line jsx-a11y/no-autofocus -- el campo aparece
        // después de enviar el formulario: el cursor tiene que ir solo, o hay
        // que buscar con el ratón un campo que no estaba hace un segundo.
        autoFocus={autoFocus}
        autoComplete={autoComplete}
        placeholder={placeholder}
        value={valor}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1.5 w-full rounded-lg border border-white/10 bg-white/[0.04] px-3.5 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 transition focus:border-sky-400/60 focus:bg-white/[0.07] focus:outline-none focus:ring-1 focus:ring-sky-400/60"
      />
      {ayuda && <span className="mt-1.5 block text-xs text-slate-500">{ayuda}</span>}
    </label>
  );
}

/**
 * Estado: dice si la API responde.
 *
 * Consulta `/health` de verdad; si no se puede, lo dice. Nunca pinta verde por
 * omisión: un indicador que miente es peor que no tenerlo.
 */
function Estado({ estado }: { estado: EstadoAPI }) {
  const texto = {
    consultando: "Consultando el estado del sistema…",
    "en-linea": "Sistema en línea",
    "sin-conexion": "Sin conexión con la API — entrar va a fallar",
  }[estado];

  const punto = {
    consultando: "bg-slate-500",
    "en-linea": "bg-emerald-400 shadow-[0_0_8px_2px_rgba(52,211,153,0.5)]",
    "sin-conexion": "bg-rose-400 shadow-[0_0_8px_2px_rgba(251,113,133,0.5)]",
  }[estado];

  const color = estado === "sin-conexion" ? "text-rose-300" : "text-slate-500";

  return (
    <div className="mt-6 flex items-center gap-2 border-t border-white/5 pt-4">
      <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${punto}`} />
      <span className={`font-mono text-[0.7rem] ${color}`}>{texto}</span>
    </div>
  );
}
